import { ArrayBuffertohex, KJUR, X509, X509CRL, zulutodate } from "jsrsasign";
import tstParse from "./tstParse";
import raizv31 from '../assets/raiz-v3.1.pem';
import crl from '../assets/tsa-v3.1.crl';

export class CAWithCRL {
    constructor(coloriuris, cert, crl) {
        this.coloriuris = coloriuris;
        this.cert = cert;
        this.crl = crl;
    }

    validate(cert) {
        const valid = this.verifySignature(cert);
        if (valid) {
            return new CAValidation(this.coloriuris, true, this.isRevoked(cert));
        } else {
            return CAValidation.notValid;
        }
    }

    verifySignature(cert) {
        return cert.verifySignature(this.cert.getPublicKey());
    }

    isRevoked(cert) {
        return this.crl.findRevCertBySN(cert.getSerialNumberHex()) !== null;
    }

    static async load(coloriuris, certUrl, crlUrl) {
        const res = await Promise.all([
            CAWithCRL.loadCA(certUrl),
            CAWithCRL.loadCRL(crlUrl)
        ]);
        return new CAWithCRL(coloriuris, res[0], res[1]);
    }

    static async loadCA(url) {
        const pem = await fetch(url);
        const pemContent = await pem.text();
        const caCert = new X509();
        caCert.readCertPEM(pemContent);
        return caCert;
    }

    static async loadCRL(url) {
        const crlHex = await readURLAsHex(url);
        const pemCRL = KJUR.asn1.ASN1Util.getPEMStringFromHex(crlHex, 'X509 CRL');
        return new X509CRL(pemCRL);
    }
}

class CAValidation {
    constructor(coloriuris, valid, revoked) {
        this.coloriuris = coloriuris;
        this.valid = valid;
        this.revoked = revoked;
    }

    static notValid = new CAValidation(false, false, false);
}

export class CAValidator {
    constructor(authorities) {
        this.authorities = authorities;
    }

    validate(cert) {
        const validation = this.authorities
            .map(ca => ca.validate(cert))
            .find(value => value.valid);
        return validation ?? CAValidation.notValid;
    }
}

export class CertInfo {
    constructor(serial, issuer, subject, notBefore, notAfter) {
        this.serial = serial;
        this.issuer = issuer;
        this.subject = subject;
        this.notBefore = notBefore;
        this.notAfter = notAfter;
    }
}

export class TimeStampTokenInfo {
    constructor(genTime, serial, alg, hash) {
        this.genTime = genTime;
        this.serial = serial;
        this.alg = alg;
        this.hash = hash;
    }
}

export class TimeStampValidation {
    constructor(certInfo, caValidation, tokenInfo) {
        this.certInfo = certInfo;
        this.caValidation = caValidation;
        this.tokenInfo = tokenInfo;
    }
}

export class TimeStampValidator {
    static async defaults() {
        //const ca = await CAWithCRL.load(true, './raiz-v3.1.pem', './tsa-v3.1.crl');
        const ca = await CAWithCRL.load(true, raizv31, crl);
        const cas = [ca];
        return new TimeStampValidator(new CAValidator(cas));
    }

    constructor(caValidator) {
        this.caValidator = caValidator;
    }

    async validate(file) {
        const hex = await readFilesAsHex(file);
        const token = tstParse(hex);

        const certs = token.certs.array;
        const cert = new X509();
        cert.readCertPEM(certs[0]);

        const certInfo = new CertInfo(
            parseInt(cert.getSerialNumberHex(), 16),
            cert.getIssuerString(),
            cert.getSubjectString(),
            zulutodate(cert.getNotBefore()),
            zulutodate(cert.getNotAfter())
        );

        const caValidation = this.caValidator.validate(cert);

        const tokenInfo = new TimeStampTokenInfo(
            zulutodate(token.econtent.content.genTime.str),
            token.econtent.content.serial.hex,
            token.econtent.content.messageImprint.alg,
            token.econtent.content.messageImprint.hash
        );
        return new TimeStampValidation(certInfo, caValidation, tokenInfo);
    }
}

async function readURLAsHex(url) {
    const buffer = await (await fetch(url)).arrayBuffer();
    return ArrayBuffertohex(buffer);
}

async function readFilesAsHex(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            const hex = ArrayBuffertohex(event.target.result);
            resolve(hex);
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
    });
}
